import { useLocation } from 'react-router-dom';
import type { QueryParamConfig } from 'use-query-params';
import {
  encodeArray, decodeArray,
  encodeString, decodeString, useQueryParams, withDefault,
} from 'use-query-params';
import type { Tag, WorkspaceSearchFull } from '@/types';

const TabParam: QueryParamConfig<WorkspaceSearchFull.SearchTab> = {
  encode: (tab: WorkspaceSearchFull.SearchTab) => encodeString(tab),
  decode: (tab: string) => decodeString(tab) as WorkspaceSearchFull.SearchTab,
};

const KeywordsParam: QueryParamConfig<string[]> = {
  encode: (keywords: string[]) => encodeArray(keywords),
  decode: (keywords: string | string[]) => {
    if (!keywords) return [];
    return decodeArray(keywords);
  },
};

const TagsParam: QueryParamConfig<Tag[]> = {
  encode: (tags: Tag[]) => encodeURIComponent(JSON.stringify(tags || [])),
  decode: (tags: string) => {
    if (!tags) return [];
    return JSON.parse(decodeURIComponent(tags)) as Tag[];
  },
};

const FileTypesParam: QueryParamConfig<WorkspaceSearchFull.File.FileTypes[]> = {
  encode: (filetypes: WorkspaceSearchFull.File.FileTypes[]) => encodeArray(filetypes),
  decode: (filetypes: string | string[]) => {
    if (!filetypes) return [];
    return decodeArray(filetypes) as WorkspaceSearchFull.File.FileTypes[];
  },
};

export default function useSearchFiltersQuery() {

  const location = useLocation();

  const currentTab = [
    'all',
    'files',
    'folders',
  ].find((tab) => location.pathname.endsWith(`/${tab}`)) as WorkspaceSearchFull.SearchTab;

  const [query, setQuery] = useQueryParams({
    keywords: KeywordsParam,
    tags: TagsParam,
    filetypes: FileTypesParam,
    tab: withDefault(TabParam, currentTab),
  });

  return [query, setQuery] as const;
}

export type FiltersQuery = ReturnType<typeof useSearchFiltersQuery>[0];

export { useSearchFiltersQuery };